import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useForm, SubmitHandler, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import Grid from '@mui/material/Grid2';

import { AppContext } from 'views/App/context';
import { translate as t } from 'hooks/useTranslations';
import FileUploader from 'components/_atoms/FileUploader';
import { Form } from 'components/_forms';
import { Organization, UpdateOrganizationAuthRequest } from 'views/App/Portal/LexID/Organizations/_types/organization';
import useManageOrganizations from 'views/App/Portal/LexID/Organizations/_data_sources/useManageOrganization';
import useOrganization, { shouldLoadOrg } from 'views/App/Portal/LexID/Organizations/_data_sources/useOrganization';
import { AUTH_TYPES } from '../../Organizations/context';
import { type ServerErrorResponse } from 'data_sources/constants';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import AttributeMapField from './AttributeMapField';
import errorHelper from 'helpers/errorHelper';

const EditOrganizationAuthentication: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentOrganizationId, hasPermission } = useContext(AppContext);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);
  const organizationQuery = useOrganization(currentOrganizationId || '', {
    enabled: shouldLoadOrg(hasPermission, currentOrganizationId),
  });

  const { updateAuth: updateOrganizationAuth } = useManageOrganizations();

  const updateOrganizationSchema = Yup.object().shape({
    authenticationType: Yup.string()
      .trim()
      .transform((value: string, originalValue: string) => {
        return value.replace(/\s+/g, ' ');
      })
      .required(`${t('organizations.properties.authenticationType')} is required`),
  });

  const {
    clearErrors,
    control,
    register,
    handleSubmit,
    formState: { errors },
    setError,
    reset,
    trigger,
    watch,
    setValue,
  } = useForm<UpdateOrganizationAuthRequest>({
    values: {
      authenticationType: 'PASSWORD',
    },
    resolver: yupResolver(updateOrganizationSchema),
  });

  React.useEffect(() => {
    if (organizationQuery.data?.organization) {
      reset({
        authenticationType:
          organizationQuery.data.cognitoPool.authenticationType === 'SSO'
            ? 'SAML'
            : organizationQuery.data.cognitoPool.authenticationType,
        attributeMap: organizationQuery.data.cognitoPool.attributeMap,
      });
    }
  }, [reset, organizationQuery.data]);

  const watchAuthenticationType = watch('authenticationType');
  const watchAttributeMap = watch('attributeMap');
  useEffect(() => {
    if (watchAuthenticationType !== 'SAML') {
      setValue('samlMetadataURL', '');
      setValue('samlMetadataFile', undefined);
    }
  }, [watchAuthenticationType]);

  const handleSave: SubmitHandler<UpdateOrganizationAuthRequest> = (values: UpdateOrganizationAuthRequest) => {
    setIsSaving(true);
    if (currentOrganizationId) {
      updateOrganizationAuth(
        currentOrganizationId,
        values.authenticationType,
        values,
        (data: Organization) => {
          queryClient.invalidateQueries(['organization', currentOrganizationId]);
          navigate('..');
        },
        (errors: ServerErrorResponse) => {
          setIsSaving(false);
          setServerErrors(errors);
        }
      );
    }
  };

  const pageTitle = `${t('organizations.auth.title')}: ${organizationQuery.data?.organization?.name}`;

  return (
    <TakeoverLayout
      pageTitle={pageTitle}
      backUrl='..'
    >
      <Box sx={{ overflowY: 'auto', minHeight: 0, p: 4 }}>
      <Card
        sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
        className='lex-form'
      >
        <Form onSubmit={handleSubmit(handleSave)}>
          <CardContent>
            {errorHelper.renderAllErrors(serverErrors)}
            <Grid
              container
              columnSpacing={4}
              rowGap={6}
            >
              <Grid size={{ xs: 12}}>
                <Controller
                  name='authenticationType'
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <FormControl fullWidth>
                      <InputLabel
                        id='authentication-type-label'
                        required
                      >
                        {t('organizations.properties.authenticationType')}
                      </InputLabel>
                      <Select
                        id='authentication_type'
                        {...register('authenticationType')}
                        label={t('organizations.properties.authenticationType')}
                        value={value}
                        onChange={onChange}
                      >
                        {AUTH_TYPES.map((x) => (
                          <MenuItem
                            key={`authenticationType_${x}`}
                            value={x}
                          >
                            {t(`organizations.authenticationTypes.${x}`)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {watchAuthenticationType === 'SAML' && (
                <>
                  <Grid size={{ xs: 12}}>
                    <Card>
                      <CardContent>
                        <Typography
                          variant='h6'
                          gutterBottom
                        >
                          SAML Configuration
                        </Typography>
                        <Grid
                          container
                          spacing={3}
                        >
                          <Grid size={{ xs: 12}}>
                            <TextField
                              id='samlMetadataURL'
                              key='samlMetadataURL'
                              label={t('organizations.properties.samlMetadataURL')}
                              variant='outlined'
                              fullWidth
                              {...register('samlMetadataURL')}
                              error={!!errors.samlMetadataURL}
                              helperText={errors.samlMetadataURL ? errors.samlMetadataURL.message : undefined}
                            />
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <Typography sx={{ textAlign: 'center', fontWeight: 'bold' }}>OR</Typography>
                          </Grid>
                          <Grid size={{ xs: 12 }}>
                            <Controller
                              name='samlMetadataFile'
                              control={control}
                              render={({ field: { onChange, value } }) => (
                                <FormControl fullWidth>
                                  <FileUploader
                                    onChange={onChange}
                                    onError={(errors) => {
                                      if (errors && errors.length > 0) {
                                        setError('samlMetadataFile', {
                                          type: 'file-type-valid',
                                          message: errors.map((x) => t(`organizations.errorMessages.${x}`)).join('; '),
                                        });
                                      } else {
                                        clearErrors('samlMetadataFile');
                                      }
                                    }}
                                    placeholder="Drag 'n' drop or click to select a SAML file"
                                    uploaderOptions={{
                                      accept: {
                                        'text/xml': ['.xml'],
                                      },
                                      maxSize: 5 * 1024 * 1024,
                                      maxFiles: 1,
                                    }}
                                  />
                                  {errors.samlMetadataFile && (
                                    <FormHelperText error>{errors.samlMetadataFile.message}</FormHelperText>
                                  )}
                                </FormControl>
                              )}
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {watchAttributeMap && Object.keys(watchAttributeMap).length > 0 && (
                    <Grid size={{ xs: 12 }}>
                      <Card>
                        <CardContent>
                          <Typography
                            variant='h6'
                            gutterBottom
                          >
                            {t('organizations.properties.attributeMap')}
                          </Typography>
                          <Controller
                            name='attributeMap'
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <AttributeMapField
                                id='attribute_map'
                                initialValues={value ?? {}}
                                onChange={onChange}
                              />
                            )}
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </CardContent>
          <CardActions>
            <Box />
            <Stack
              direction='row'
              spacing={2}
            >
              <Button
                color='neutral'
                href='..'
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  trigger();
                }}
                disabled={isSaving}
                component='button'
                type='submit'
                variant='contained'
              >
                {t('organizations.auth.save')}
              </Button>
            </Stack>
          </CardActions>
        </Form>
      </Card>
      </Box>
    </TakeoverLayout>
  );
};

export default EditOrganizationAuthentication;
